/**
 * Budget Comparison Spreadsheet Module
 * Clean spreadsheet-style comparison and editing of budget versions
 */

import { 
  getBudgetVersions, 
  loadBudgetVersionData, 
  saveCurrentVersion,
  saveBudgetVersion,
  saveUserData,
  updateVersionsList,
  getCurrentUser,
  setSyncing,
  setSynced,
  setSyncError,
  setDefaultVersion
} from './budget.js';
import { formatCurrency, parseCurrencyInput } from './utils/currency.js';

/**
 * Safely evaluate math expressions
 * Supports basic operations: +, -, *, /, (, )
 * Returns null if invalid expression
 */
function evaluateMathExpression(expression) {
  if (!expression || typeof expression !== 'string') return null;
  
  // Remove spaces and validate characters (only allow numbers, operators, parentheses, decimal points)
  const cleanExpression = expression.replace(/\s/g, '');
  if (!/^[0-9+\-*/().]+$/.test(cleanExpression)) return null;
  
  // Prevent division by zero and other invalid patterns
  if (/\/0(?!\.)/.test(cleanExpression)) return null;
  if (/[+\-*/]{2,}/.test(cleanExpression)) return null; // No consecutive operators
  if (/^[+\-*/]/.test(cleanExpression) && !/^[-]/.test(cleanExpression)) return null; // No leading operators except minus
  if (/[+\-*/]$/.test(cleanExpression)) return null; // No trailing operators
  
  try {
    // Use Function constructor for safe evaluation (better than eval)
    const result = Function(`"use strict"; return (${cleanExpression})`)();
    
    // Validate result is a finite number
    if (typeof result !== 'number' || !isFinite(result)) return null;
    
    return Math.round(result * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    return null;
  }
}

/**
 * State management
 */
let baseVersionData = null;
let editVersionData = null;
let isInitialized = false;
let hasUnsavedChanges = false;
let currentEditVersionId = null;

/**
 * Initialize the comparison view
 */
export async function initializeComparison() {
  if (isInitialized) return;
  
  console.log('Initializing spreadsheet comparison view...');
  
  try {
    setupEventListeners();
    await loadVersionOptions();
    
    isInitialized = true;
    console.log('Spreadsheet comparison view initialized');
  } catch (error) {
    console.error('Failed to initialize comparison view:', error);
  }
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
  const baseSelect = document.getElementById('base-version-select');
  const editSelect = document.getElementById('edit-version-select');
  const createNewBtn = document.getElementById('create-new-version');
  const saveBtn = document.getElementById('save-comparison-version');
  const confirmNewBtn = document.getElementById('confirm-new-version');
  const cancelNewBtn = document.getElementById('cancel-new-version');
  
  if (baseSelect) {
    baseSelect.addEventListener('change', handleBaseVersionSelection);
  }
  
  if (editSelect) {
    editSelect.addEventListener('change', handleEditVersionSelection);
  }
  
  if (createNewBtn) {
    createNewBtn.addEventListener('click', showNewVersionDialog);
  }
  
  if (saveBtn) {
    saveBtn.addEventListener('click', handleSaveChanges);
  }
  
  if (confirmNewBtn) {
    confirmNewBtn.addEventListener('click', handleConfirmNewVersion);
  }
  
  if (cancelNewBtn) {
    cancelNewBtn.addEventListener('click', hideNewVersionDialog);
  }
}

/**
 * Load available budget versions into dropdown options
 */
async function loadVersionOptions() {
  try {
    const versions = await getBudgetVersions();
    const baseSelect = document.getElementById('base-version-select');
    const editSelect = document.getElementById('edit-version-select');
    
    if (!baseSelect || !editSelect) return;
    
    // Clear existing options
    baseSelect.innerHTML = '<option value="">Select base version...</option>';
    editSelect.innerHTML = '<option value="">Select version to edit...</option>';
    
    // Add version options
    versions.forEach(version => {
      const baseOption = document.createElement('option');
      baseOption.value = version.id;
      baseOption.textContent = `${version.name} (${formatLastModified(version.lastModified)})`;
      baseSelect.appendChild(baseOption);
      
      const editOption = document.createElement('option');
      editOption.value = version.id;
      editOption.textContent = `${version.name} (${formatLastModified(version.lastModified)})`;
      editSelect.appendChild(editOption);
    });
    
    console.log(`Loaded ${versions.length} versions into comparison dropdowns`);
  } catch (error) {
    console.error('Failed to load version options:', error);
  }
}

/**
 * Format the last modified date for display
 */
function formatLastModified(timestamp) {
  if (!timestamp) return 'Unknown';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

/**
 * Handle base version selection
 */
async function handleBaseVersionSelection(event) {
  const versionId = event.target.value;
  
  if (!versionId) {
    baseVersionData = null;
    updateDisplay();
    return;
  }
  
  try {
    baseVersionData = await loadBudgetVersionData(versionId);
    console.log('Base version data loaded:', baseVersionData);
    updateVersionHeaders();
    updateDisplay();
  } catch (error) {
    console.error(`Failed to load base version ${versionId}:`, error);
  }
}

/**
 * Handle edit version selection
 */
async function handleEditVersionSelection(event) {
  const versionId = event.target.value;
  
  if (!versionId) {
    editVersionData = null;
    currentEditVersionId = null;
    updateDisplay();
    return;
  }
  
  try {
    editVersionData = await loadBudgetVersionData(versionId);
    console.log('Edit version data loaded:', editVersionData);
    currentEditVersionId = versionId;
    hasUnsavedChanges = false;
    updateVersionHeaders();
    updateDisplay();
    updateSaveButton();
  } catch (error) {
    console.error(`Failed to load edit version ${versionId}:`, error);
  }
}

/**
 * Update version headers in table
 */
function updateVersionHeaders() {
  const baseHeader = document.getElementById('base-version-header');
  const editHeader = document.getElementById('edit-version-header');
  
  if (baseHeader && baseVersionData) {
    const baseSelect = document.getElementById('base-version-select');
    const selectedOption = baseSelect.options[baseSelect.selectedIndex];
    baseHeader.textContent = selectedOption.text.split(' (')[0]; // Get just the version name
  }
  
  if (editHeader && editVersionData) {
    const editSelect = document.getElementById('edit-version-select');
    const selectedOption = editSelect.options[editSelect.selectedIndex];
    editHeader.textContent = selectedOption.text.split(' (')[0]; // Get just the version name
  }
}

/**
 * Show new version dialog
 */
function showNewVersionDialog() {
  const dialog = document.getElementById('new-version-dialog');
  const input = document.getElementById('new-version-name');
  
  if (dialog && input) {
    dialog.style.display = 'flex';
    input.value = '';
    input.focus();
  }
}

/**
 * Hide new version dialog
 */
function hideNewVersionDialog() {
  const dialog = document.getElementById('new-version-dialog');
  if (dialog) {
    dialog.style.display = 'none';
  }
}

/**
 * Handle confirming new version creation
 */
async function handleConfirmNewVersion() {
  const input = document.getElementById('new-version-name');
  const versionName = input.value.trim();
  
  if (!versionName) {
    alert('Please enter a version name');
    return;
  }
  
  try {
    // Create new version based on base version or empty template
    const newVersionData = baseVersionData ? 
      JSON.parse(JSON.stringify(baseVersionData)) : 
      { income: [], categories: {} };
    
    // Save the new version to the budget system immediately
    await saveBudgetVersion(versionName, newVersionData);
    
    editVersionData = newVersionData;
    currentEditVersionId = versionName;
    hasUnsavedChanges = false; // Version is already saved
    
    // Refresh version options to include the new version
    await loadVersionOptions();
    
    // Select the new version in the edit dropdown
    const editSelect = document.getElementById('edit-version-select');
    if (editSelect) {
      editSelect.value = versionName;
    }
    
    hideNewVersionDialog();
    updateVersionHeaders();
    updateDisplay();
    updateSaveButton();
    
    console.log(`Created and saved new version: ${versionName}`);
    
    // Ask user if they want to set this as the default budget
    const setAsDefault = confirm(`Version "${versionName}" created successfully!\n\nWould you like to set this as your default budget?`);
    if (setAsDefault) {
      setDefaultVersion(versionName);
    }
  } catch (error) {
    console.error('Failed to create new version:', error);
    alert('Failed to create new version. Please try again.');
  }
}

/**
 * Update the main display
 */
function updateDisplay() {
  const container = document.getElementById('spreadsheet-container');
  const emptyState = document.getElementById('comparison-empty-state');
  
  if (!container || !emptyState) return;
  
  if (!baseVersionData || !editVersionData) {
    container.style.display = 'none';
    emptyState.style.display = 'block';
    return;
  }
  
  container.style.display = 'block';
  emptyState.style.display = 'none';
  
  buildSpreadsheetTable();
  updateTotals();
}

/**
 * Build the spreadsheet table
 */
function buildSpreadsheetTable() {
  const tableBody = document.getElementById('comparison-table-body');
  if (!tableBody) return;
  
  tableBody.innerHTML = '';
  
  console.log('Building spreadsheet table...');
  console.log('Base version data structure:', baseVersionData);
  console.log('Edit version data structure:', editVersionData);
  
  // Add Income section
  addCategoryHeader(tableBody, 'Income', 'income');
  addIncomeRows(tableBody);
  
  // Add expense categories
  const baseCategories = baseVersionData?.categories || {};
  const editCategories = editVersionData?.categories || {};
  console.log('Base categories:', baseCategories);
  console.log('Edit categories:', editCategories);
  
  const allCategoryKeys = new Set([
    ...Object.keys(baseCategories),
    ...Object.keys(editCategories)
  ]);
  
  console.log('All category keys found:', Array.from(allCategoryKeys));
  
  Array.from(allCategoryKeys).sort().forEach(categoryKey => {
    const baseCategory = baseCategories[categoryKey];
    const editCategory = editCategories[categoryKey];
    
    // Get the actual category name from the category object
    const categoryName = baseCategory?.name || editCategory?.name || categoryKey;
    
    console.log(`Processing category key: "${categoryKey}", name: "${categoryName}"`);
    addCategoryHeader(tableBody, categoryName, 'expenses');
    addCategoryRows(tableBody, categoryKey, categoryName);
  });
}

/**
 * Add category header row
 */
function addCategoryHeader(container, categoryName, type) {
  const headerDiv = document.createElement('div');
  headerDiv.className = `category-header ${type}`;
  
  const contentDiv = document.createElement('div');
  contentDiv.className = 'category-header-content';
  contentDiv.innerHTML = `
    <div class="category-title">${categoryName}</div>
    <button class="add-item-btn" data-category="${categoryName}" data-type="${type}">+Add Item</button>
  `;
  
  headerDiv.appendChild(contentDiv);
  container.appendChild(headerDiv);
  
  // Add event listener for add item button
  const addBtn = contentDiv.querySelector('.add-item-btn');
  if (addBtn) {
    addBtn.addEventListener('click', handleAddItem);
  }
}

/**
 * Add income rows
 */
function addIncomeRows(container) {
  const baseIncome = baseVersionData?.income || [];
  const editIncome = editVersionData?.income || [];
  
  console.log('Base income:', baseIncome);
  console.log('Edit income:', editIncome);
  
  // Get all unique income names (using 'label' property)
  const allIncomeNames = new Set([
    ...baseIncome.map(item => item?.label || item?.name || 'unnamed'),
    ...editIncome.map(item => item?.label || item?.name || 'unnamed')
  ]);
  
  console.log('All income names found:', Array.from(allIncomeNames));
  
  Array.from(allIncomeNames).sort().forEach(itemName => {
    const baseItem = baseIncome.find(item => (item?.label || item?.name) === itemName);
    const editItem = editIncome.find(item => (item?.label || item?.name) === itemName);
    console.log(`Processing income item: "${itemName}", base:`, baseItem, 'edit:', editItem);
    addSpreadsheetRow(container, 'income', '', itemName, baseItem, editItem);
  });
}

/**
 * Add category rows
 */
function addCategoryRows(container, categoryKey, categoryName) {
  const baseCategory = baseVersionData?.categories?.[categoryKey];
  const editCategory = editVersionData?.categories?.[categoryKey];
  
  console.log(`Category "${categoryName}" (key: ${categoryKey}) - base:`, baseCategory, 'edit:', editCategory);
  
  const baseItems = baseCategory?.items || [];
  const editItems = editCategory?.items || [];
  
  console.log(`Items for category "${categoryName}" - base:`, baseItems, 'edit:', editItems);
  
  // Get all unique item names (using 'label' property)
  const allItemNames = new Set([
    ...baseItems.map(item => item?.label || item?.name || 'unnamed'),
    ...editItems.map(item => item?.label || item?.name || 'unnamed')
  ]);
  
  console.log(`All item names for category "${categoryName}":`, Array.from(allItemNames));
  
  Array.from(allItemNames).sort().forEach(itemName => {
    const baseItem = baseItems.find(item => (item?.label || item?.name) === itemName);
    const editItem = editItems.find(item => (item?.label || item?.name) === itemName);
    console.log(`Processing item: "${itemName}" in category "${categoryName}", base:`, baseItem, 'edit:', editItem);
    addSpreadsheetRow(container, 'expense', categoryKey, itemName, baseItem, editItem);
  });
}

/**
 * Add a spreadsheet row
 */
function addSpreadsheetRow(container, type, categoryKeyOrName, itemName, baseItem, editItem) {
  const baseAmount = baseItem ? parseFloat(baseItem.amount) || 0 : 0;
  const editAmount = editItem ? parseFloat(editItem.amount) || 0 : 0;
  const difference = editAmount - baseAmount;
  
  // Helper function to generate status icons for a specific item
  function generateStatusIcons(item) {
    if (!item) return '';
    
    const icons = [];
    
    // Savings indicator (first position to match main budget view)
    if (item.isSavings) {
      icons.push('<span class="status-icon savings" title="Savings item">💰</span>');
    }
    
    // Recurring indicator
    if (item.isRecurring) {
      icons.push('<span class="status-icon recurring" title="Recurring expense">🔄</span>');
    }
    
    // Tax deductible indicator
    if (item.taxDeductible || item.isTaxDeductible) {
      icons.push('<span class="status-icon tax-deductible" title="Tax deductible">🧾</span>');
    }
    
    // Non-monthly frequency indicator
    const frequency = item.frequency || 'monthly';
    if (frequency !== 'monthly') {
      const frequencyIcons = {
        'weekly': '📅',
        'bi-weekly': '📅',
        'quarterly': '🗓️', 
        'annual': '📆',
        'one-time': '➿'
      };
      const icon = frequencyIcons[frequency] || '⏱️';
      const label = frequency.charAt(0).toUpperCase() + frequency.slice(1);
      icons.push(`<span class="status-icon frequency" title="${label} frequency">${icon}</span>`);
    }
    
    return icons.length > 0 ? `<div class="item-status-icons">${icons.join('')}</div>` : '';
  }
  
  // Generate icons for base and edit versions separately
  const baseStatusIcons = generateStatusIcons(baseItem);
  const editStatusIcons = generateStatusIcons(editItem);
  
  const row = document.createElement('div');
  row.className = 'spreadsheet-row';
  row.innerHTML = `
    <div class="item-name-container">
      <div class="item-name" contenteditable="true" data-type="${type}" data-category="${categoryKeyOrName}" data-original-name="${itemName}">${itemName}</div>
    </div>
    <div class="base-amount-container">
      ${baseStatusIcons}
      <div class="base-amount">${baseAmount > 0 ? formatCurrency(baseAmount) : '—'}</div>
    </div>
    <div class="edit-amount-container">
      ${editStatusIcons}
      <input type="text" value="${editAmount}" 
             data-type="${type}" data-category="${categoryKeyOrName}" data-item="${itemName}">
    </div>
    <div class="difference-amount ${difference > 0 ? 'positive' : difference < 0 ? 'negative' : ''}">${Math.abs(difference) > 0.01 ? formatCurrency(Math.abs(difference)) : '—'}</div>
  `;
  
  container.appendChild(row);
  
  // Add event listeners
  const nameElement = row.querySelector('.item-name');
  const amountInput = row.querySelector('input');
  
  if (nameElement) {
    nameElement.addEventListener('blur', handleNameChange);
    nameElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.target.blur();
      }
    });
  }
  
  if (amountInput) {
    amountInput.addEventListener('input', handleAmountInput);
    amountInput.addEventListener('change', handleAmountChange);
    
    // Add math expression tooltip
    amountInput.setAttribute('title', 'Supports math expressions (e.g., 10+7, 25*4, 100/2)');
    amountInput.setAttribute('placeholder', '0.00');
  }
}

/**
 * Handle item name changes
 */
function handleNameChange(event) {
  const element = event.target;
  const newName = element.textContent.trim();
  const originalName = element.getAttribute('data-original-name');
  const type = element.getAttribute('data-type');
  const categoryKey = element.getAttribute('data-category');
  
  if (newName === originalName || !newName) return;
  
  if (type === 'income') {
    const item = editVersionData.income?.find(item => (item.label || item.name) === originalName);
    if (item) {
      // Update the label property (preferred) or name as fallback
      if (item.label !== undefined) {
        item.label = newName;
      } else {
        item.name = newName;
      }
      element.setAttribute('data-original-name', newName);
      markAsChanged();
    }
  } else if (type === 'expense') {
    const category = editVersionData.categories?.[categoryKey];
    const item = category?.items?.find(item => (item.label || item.name) === originalName);
    if (item) {
      // Update the label property (preferred) or name as fallback
      if (item.label !== undefined) {
        item.label = newName;
      } else {
        item.name = newName;
      }
      element.setAttribute('data-original-name', newName);
      markAsChanged();
    }
  }
}

/**
 * Handle real-time input for visual feedback
 */
function handleAmountInput(event) {
  const input = event.target;
  const inputValue = input.value.trim();
  
  // Remove all math-related classes first
  input.classList.remove('math-expression', 'math-valid');
  
  if (!inputValue) return;
  
  // Check if it looks like a math expression (contains operators)
  const hasMathOperators = /[+\-*/()]/.test(inputValue);
  
  if (hasMathOperators) {
    input.classList.add('math-expression');
    
    // Check if the expression is valid
    const mathResult = evaluateMathExpression(inputValue);
    if (mathResult !== null) {
      input.classList.add('math-valid');
    }
  }
}

/**
 * Handle amount changes
 */
function handleAmountChange(event) {
  const input = event.target;
  let inputValue = input.value.trim();
  let amount = 0;
  
  // Try to evaluate as math expression first
  const mathResult = evaluateMathExpression(inputValue);
  if (mathResult !== null) {
    amount = mathResult;
    // Update the input field to show the calculated result
    input.value = amount.toString();
  } else {
    // Fall back to regular number parsing
    amount = parseFloat(inputValue) || 0;
  }
  
  const type = input.getAttribute('data-type');
  const categoryKey = input.getAttribute('data-category');
  const itemName = input.getAttribute('data-item');
  
  if (type === 'income') {
    if (!editVersionData.income) {
      editVersionData.income = [];
    }
    
    let item = editVersionData.income.find(item => (item.label || item.name) === itemName);
    if (!item) {
      item = { label: itemName, amount: amount };
      editVersionData.income.push(item);
    } else {
      item.amount = amount;
    }
  } else if (type === 'expense') {
    if (!editVersionData.categories) {
      editVersionData.categories = {};
    }
    if (!editVersionData.categories[categoryKey]) {
      editVersionData.categories[categoryKey] = { items: [] };
    }
    
    let item = editVersionData.categories[categoryKey].items.find(item => (item.label || item.name) === itemName);
    if (!item) {
      item = { label: itemName, amount: amount };
      editVersionData.categories[categoryKey].items.push(item);
    } else {
      item.amount = amount;
    }
  }
  
  input.classList.add('changed');
  markAsChanged();
  updateRowDifference(input.closest('.spreadsheet-row'));
  updateTotals();
}

/**
 * Update difference for a specific row
 */
function updateRowDifference(row) {
  const baseAmountText = row.querySelector('.base-amount').textContent;
  const editAmountInput = row.querySelector('input');
  const differenceElement = row.querySelector('.difference-amount');
  
  const baseAmount = baseAmountText === '—' ? 0 : parseFloat(baseAmountText.replace(/[$,]/g, '')) || 0;
  const editAmount = parseFloat(editAmountInput.value) || 0;
  const difference = editAmount - baseAmount;
  
  differenceElement.textContent = Math.abs(difference) > 0.01 ? formatCurrency(Math.abs(difference)) : '—';
  differenceElement.className = 'difference-amount';
  
  if (difference > 0) {
    differenceElement.classList.add('positive');
  } else if (difference < 0) {
    differenceElement.classList.add('negative');
  }
}

/**
 * Handle adding new items
 */
function handleAddItem(event) {
  const button = event.target;
  const categoryName = button.getAttribute('data-category');
  const type = button.getAttribute('data-type');
  
  const itemName = prompt(`Enter ${type === 'income' ? 'income source' : 'expense item'} name:`);
  if (!itemName?.trim()) return;
  
  if (type === 'income') {
    if (!editVersionData.income) {
      editVersionData.income = [];
    }
    
    // Check if item already exists
    if (editVersionData.income.some(item => item.name === itemName.trim())) {
      alert('An income source with this name already exists.');
      return;
    }
    
    editVersionData.income.push({
      name: itemName.trim(),
      amount: '0'
    });
  } else {
    if (!editVersionData.categories) {
      editVersionData.categories = [];
    }

    // categories is an array — find the entry whose name matches
    let category = editVersionData.categories.find(c => (c.name || c.categoryName) === categoryName);
    if (!category) {
      category = { name: categoryName, items: [] };
      editVersionData.categories.push(category);
    }
    if (!category.items) category.items = [];

    // Check if item already exists
    if (category.items.some(item => (item.label || item.name) === itemName.trim())) {
      alert('An item with this name already exists in this category.');
      return;
    }

    category.items.push({
      name: itemName.trim(),
      amount: '0'
    });
  }
  
  markAsChanged();
  buildSpreadsheetTable();
  updateTotals();
}

/**
 * Update totals section
 */
function updateTotals() {
  const baseTotals = calculateTotals(baseVersionData);
  const editTotals = calculateTotals(editVersionData);
  
  updateTotalRow('income', baseTotals.totalIncome, editTotals.totalIncome);
  updateTotalRow('expenses', baseTotals.totalExpenses, editTotals.totalExpenses);
  updateTotalRow('net', baseTotals.netAmount, editTotals.netAmount);
}

/**
 * Update a total row
 */
function updateTotalRow(type, baseValue, editValue) {
  const baseElement = document.getElementById(`total-${type}-base`);
  const editElement = document.getElementById(`total-${type}-edit`);
  const diffElement = document.getElementById(`total-${type}-diff`);
  
  if (!baseElement || !editElement || !diffElement) return;
  
  baseElement.textContent = formatCurrency(baseValue);
  editElement.textContent = formatCurrency(editValue);
  
  const difference = editValue - baseValue;
  diffElement.textContent = formatCurrency(Math.abs(difference));
  diffElement.className = 'totals-difference';
  
  if (difference > 0) {
    diffElement.classList.add('positive');
  } else if (difference < 0) {
    diffElement.classList.add('negative');
  }
}

/**
 * Calculate totals from budget data
 */
function calculateTotals(budgetData) {
  let totalIncome = 0;
  let totalExpenses = 0;
  
  // Calculate income
  if (budgetData?.income) {
    totalIncome = budgetData.income.reduce((sum, item) => {
      return sum + (parseFloat(item.amount) || 0);
    }, 0);
  }
  
  // Calculate expenses
  if (budgetData?.categories) {
    Object.values(budgetData.categories).forEach(category => {
      if (category.items) {
        category.items.forEach(item => {
          totalExpenses += parseFloat(item.amount) || 0;
        });
      }
    });
  }
  
  return {
    totalIncome,
    totalExpenses,
    netAmount: totalIncome - totalExpenses
  };
}

/**
 * Mark as changed and update UI
 */
function markAsChanged() {
  hasUnsavedChanges = true;
  updateSaveButton();
}

/**
 * Update save button visibility
 */
function updateSaveButton() {
  const saveBtn = document.getElementById('save-comparison-version');
  if (saveBtn) {
    saveBtn.style.display = hasUnsavedChanges ? 'block' : 'none';
  }
}

/**
 * Handle saving changes
 */
async function handleSaveChanges() {
  if (!editVersionData || !currentEditVersionId) return;
  
  try {
    console.log('Saving changes to version:', currentEditVersionId);
    console.log('Edit version data to save:', editVersionData);
    
    // Save the budget version using the budget system
    await saveBudgetVersion(currentEditVersionId, editVersionData);
    
    hasUnsavedChanges = false;
    updateSaveButton();
    
    // Refresh the version options to show the updated version
    await loadVersionOptions();
    
    alert('Changes saved successfully!');
    
  } catch (error) {
    console.error('Failed to save changes:', error);
    alert('Failed to save changes. Please try again.');
  }
}

/**
 * Refresh the comparison view
 */
export async function refreshComparison() {
  console.log('Refreshing comparison view...');
  
  await loadVersionOptions();
  
  baseVersionData = null;
  editVersionData = null;
  currentEditVersionId = null;
  hasUnsavedChanges = false;
  
  const baseSelect = document.getElementById('base-version-select');
  const editSelect = document.getElementById('edit-version-select');
  
  if (baseSelect) baseSelect.value = '';
  if (editSelect) editSelect.value = '';
  
  updateDisplay();
  updateSaveButton();
}

/**
 * Main function to update comparison
 */
export async function updateComparison() {
  console.log('Updating comparison view...');
  await initializeComparison();
}